import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/Reveal";
import { renderRichText } from "./ContentPage";
import { FAQ_ITEMS } from "../data/faq";

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div data-testid="faq-page">
      <SEO
        title="FAQ | Lunar Ambitions"
        description="Frequently asked questions about facials, nano needling, dermaplaning, booking, and appointments at Lunar Ambitions in Orlando, FL."
        extraSchema={FAQ_SCHEMA}
      />
      <h1 className="sr-only">FAQ</h1>
      <section className="relative overflow-hidden bg-white pt-52 pb-24 lg:pt-72 sm:pb-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="divide-y divide-navy/10 border-y border-navy/10">
              {FAQ_ITEMS.map((item, i) => {
                const open = openIndex === i;
                return (
                  <div key={i} data-testid={`faq-item-${i}`}>
                    <h2 className="contents">
                      <button
                        onClick={() => setOpenIndex(open ? -1 : i)}
                        aria-expanded={open}
                        data-testid={`faq-question-${i}`}
                        className="group w-full flex items-center justify-between gap-6 py-6 text-left"
                      >
                        <span
                          className={`font-display text-lg sm:text-xl leading-snug transition-colors duration-300 ${
                            open ? "text-gold" : "text-navy group-hover:text-gold"
                          }`}
                        >
                          {item.q}
                        </span>
                        <motion.span
                          animate={{ rotate: open ? 45 : 0 }}
                          transition={{ duration: 0.3 }}
                          className={`shrink-0 transition-colors duration-300 ${
                            open ? "text-gold" : "text-navy/40 group-hover:text-gold"
                          }`}
                        >
                          <Plus size={20} strokeWidth={1.5} />
                        </motion.span>
                      </button>
                    </h2>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p
                            className="pb-7 pr-10 text-base text-navy/70 leading-relaxed font-light border-l-2 border-gold pl-5 ml-1"
                            data-testid={`faq-answer-${i}`}
                          >
                            {renderRichText(item.a, item.links)}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Faq;
